import type { ArticleType, PublicArticle } from "./article";
import type { DeepLinkStaticPath } from "./deep-links";
import type { FlowEvent } from "./flows";
import {
  BLOOD_TEST_PREPARATION_ARTICLE_KEY,
  BP_PULSE_GUIDE_ARTICLE_KEY,
} from "./medical-evidence";

// TES-591 — the three access classes, and what a patient
// without a verified subscription may see of member content.
//
//   CARE_ACCESS                 — necessary care before the subscription:
//                                 F-01 result, F-02, the consult, requested
//                                 evidence, lab tasks, BP/pulse, published
//                                 results and decisions, account/booking.
//   SUBSCRIPTION_CONTENT_ACCESS — "Stöd" and the member parts of "Hälsa": full
//                                 articles, recipes, courses, training,
//                                 DT deepenings, reflections, saved items,
//                                 coach tools. Opened ONLY by a verified
//                                 subscription (Stripe trialing/active) —
//                                 never by a booked or paid consult, a
//                                 positive decision, the activation mail,
//                                 an opened checkout or an unconfirmed
//                                 payment.
//   JOURNEY_HISTORY_ACCESS      — Din resa: always, from account creation,
//                                 independent of the subscription.
//
// The lock is enforced server-side (list previews, 403 on detail and member
// tools); the app's lock symbol and dimming are a courtesy on top.
export const ACCESS_CLASS = {
  CARE: "CARE_ACCESS",
  SUBSCRIPTION_CONTENT: "SUBSCRIPTION_CONTENT_ACCESS",
  JOURNEY_HISTORY: "JOURNEY_HISTORY_ACCESS",
} as const;
export type AccessClass = (typeof ACCESS_CLASS)[keyof typeof ACCESS_CLASS];

/**
 * Article types that stay unauthenticated: the landing "Artiklar" module on
 * ella.se (`article`) and UI copy blocks (`support_copy`). Everything else a
 * patient reads in "Stöd" is subscription content; `clinical_result` is care
 * (bound to the doctor's decision assignment, never listed).
 */
export const PUBLIC_ARTICLE_TYPES: readonly ArticleType[] = [
  "article",
  "support_copy",
];

/**
 * TES-631 §6 — the preparation for a booked consultation.
 *
 * The "Förbered dig inför mötet" CTA on the home screen opened this article,
 * and because access is classified by article TYPE, a checklist counts as
 * member content and the patient hit the subscription wall. She had booked and
 * paid for the consultation this checklist prepares her for.
 *
 * So it is care, and it is classified by KEY rather than by type — because the
 * type is right for every other checklist. The unlock is not unconditional
 * either: it holds while she has a consultation booked and not yet held, which
 * is exactly the window in which "prepare for your meeting" means anything.
 * `meetingPrepAccessClass` in the API applies that condition; this constant is
 * shared so the app, the API and the home-now CTA cannot disagree about which
 * article it is.
 */
export const MEETING_PREP_ARTICLE_KEY = "onboarding.prepare_doctor_meeting";

/**
 * Emelie, QA 2026-09-21 (report 1) — the explainer BEFORE F-01.
 *
 * Home shows "Varför börjar vi med frågor?" with the CTA
 * "Läs om första steget" for a patient who has not answered F-01 yet. The
 * CTA opens `onboarding.first_step` ("Så fungerar första steget"), authored
 * as a `mini_lesson` — and every type outside PUBLIC_ARTICLE_TYPES is member
 * content, so she was shown "Ingår i ditt abonnemang" and an offer to
 * subscribe. Her words: "Patienten befinner sig före F-01 och kan inte ha"
 * "ett abonnemang ännu."
 *
 * So, like the consultation preparation above, this is classified by KEY
 * rather than by type. Unlike it, the unlock carries no window: an article
 * whose whole subject is the step before the journey starts is never member
 * content, in any state. The type stays `mini_lesson` because that is what it
 * is — the lesson chrome is correct, the paywall was not.
 */
export const ONBOARDING_FIRST_STEP_ARTICLE_KEY = "onboarding.first_step";

/**
 * Articles that are open regardless of what their TYPE says, with no
 * condition to check. `MEETING_PREP_ARTICLE_KEY` is deliberately NOT here:
 * its unlock holds only while a consultation is booked, so it stays with the
 * caller that can ask that question.
 *
 * TES-1313 — "Så mäter du blodtryck och vilopuls" is the instruction for a
 * CP-06 task. BP/pulse is CARE_ACCESS (above), and the patient asked for it
 * may have no subscription yet, so the page is never member content.
 */
/**
 * TES-1348 — CP-01 §5 "Nästa steg efter din första återkoppling" → "Läs mer"
 * (TES-1346) opens this lesson before the consultation is even booked, when no
 * subscription can exist yet.
 */
export const ONBOARDING_AFTER_FEEDBACK_ARTICLE_KEY =
  "onboarding.after_feedback";

export function isAlwaysOpenArticleKey(key: string): boolean {
  return (
    key === ONBOARDING_FIRST_STEP_ARTICLE_KEY ||
    key === INJECTION_GUIDE_ARTICLE_KEY ||
    // TES-1348 — "Så förbereder du dig" is opened from the CP-06 blood-test
    // card, which a patient meets before the treatment decision and so before
    // any subscription. Its `checklist` type put Kirill's preparation
    // instructions behind the paywall.
    key === BLOOD_TEST_PREPARATION_ARTICLE_KEY ||
    key === ONBOARDING_AFTER_FEEDBACK_ARTICLE_KEY ||
    key === BP_PULSE_GUIDE_ARTICLE_KEY
  );
}

/**
 * TES-1068 — CARE-GUIDE-01 "Så tar du din injektion".
 *
 * One persistent object, opened from "Din vård → Aktuell behandling" and from
 * CP-12's "För dig idag" card. Canon (CP-12 document, 2026-10-02):
 * "Abonnemangsnivå eller coachåtkomst får inte låsa guiden." It is safety
 * information about the patient's own prescribed medicine, so it is care,
 * not member content — classified by KEY like `onboarding.first_step`, with
 * no window. Who is shown the entry is the app's call (a current
 * prescription); the content itself is generic and never patient-specific.
 */
export const INJECTION_GUIDE_ARTICLE_KEY = "guide.care.how_to_inject";

/**
 * The article "Din första injektion – steg för steg" that CP-12 linked to
 * before TES-1068. Canon makes the injection guide its replacement (it must
 * not be a temporary CP-12 article), so the app opens the guide for either
 * key; the old row stays published for older builds.
 */
export const LEGACY_FIRST_INJECTION_ARTICLE_KEY =
  "treatment_start.injection_guide";

export function articleAccessClass(type: ArticleType): AccessClass | "PUBLIC" {
  if (type === "clinical_result") return ACCESS_CLASS.CARE;
  if (PUBLIC_ARTICLE_TYPES.includes(type)) return "PUBLIC";
  return ACCESS_CLASS.SUBSCRIPTION_CONTENT;
}

export function isSubscriptionContentType(type: ArticleType): boolean {
  return articleAccessClass(type) === ACCESS_CLASS.SUBSCRIPTION_CONTENT;
}

/**
 * TES-946 — the app destinations whose content is SUBSCRIPTION_CONTENT_ACCESS,
 * keyed by deep-link path.
 *
 * Emelie's rule (TES-840 §7): "CTA:er får inte leda till abonnemangslåst eller fel fas".
 * Home CTAs are copied into a patient's assignment when it is created, so a
 * card authored before its destination was locked keeps pointing there. The
 * API reads this table to withhold such a card until she has access; before
 * this, which destinations were locked lived only in the screens themselves
 * (each checks `access.subscriptionContent`), with nothing either side could
 * read.
 *
 * A destination is listed when what the patient comes there to SEE is member
 * content. Its value is the care actions that stay open on the same screen:
 * a task completed by one of those events sends her there to DO something the
 * lock does not cover, so it is never withheld. `health/trends` is the case in
 * point: its history and charts are locked, but recording weight and blood
 * pressure + pulse is care (CARE_ACCESS above), and every CP-06 variant sends
 * pre-decision patients there to record their vitals.
 *
 * Hubs (`health/home`, `support/nutrition`, `support/training`) are not
 * listed: they show a lock card but also open care and navigation, so a CTA
 * to one never lands on nothing but a lock.
 */
export const SUBSCRIPTION_LOCKED_DESTINATIONS: Readonly<
  Partial<Record<DeepLinkStaticPath, readonly FlowEvent[]>>
> = {
  "health/trends": ["weight_logged", "vitals_recorded"],
  "support/nutrition/recipes": [],
  "support/nutrition/plans": [],
  "support/nutrition/knowledge": [],
  "support/training/passes": [],
  "support/training/plans": [],
  "support/training/knowledge": [],
};

const CTA_SCHEME = "carebyella://";

/**
 * Does this CTA lead to content the patient can open only with a
 * subscription? `completesOn` is the task's completion event, when the item
 * is a task; see SUBSCRIPTION_LOCKED_DESTINATIONS for why it matters.
 *
 * Only exact static paths match: an authored ctaPath is validated against the
 * deep-link table, so a variant spelling is not a destination at all.
 * Articles are out of scope here; their access is decided per article
 * (`articleAccessClass`), not per destination.
 */
export function isSubscriptionLockedCta(
  ctaPath: string | null | undefined,
  completesOn?: string | null,
): boolean {
  if (!ctaPath || !ctaPath.toLowerCase().startsWith(CTA_SCHEME)) return false;
  const path = ctaPath.slice(CTA_SCHEME.length) as DeepLinkStaticPath;
  if (
    !Object.prototype.hasOwnProperty.call(
      SUBSCRIPTION_LOCKED_DESTINATIONS,
      path,
    )
  ) {
    return false;
  }
  const openActions: readonly string[] =
    SUBSCRIPTION_LOCKED_DESTINATIONS[path] ?? [];
  return !(completesOn && openActions.includes(completesOn));
}

/** Emelie's canonical lock copy (2026-09-09). Swedish verbatim; English ours. */
export const SUBSCRIPTION_LOCK_COPY = {
  sv: {
    title: "Ingår i ditt abonnemang",
    body: "När ditt abonnemang är aktiverat får du tillgång till hela Stöd och Hälsa, med kost, träning, beteendestöd och verktyg för att följa din hälsa över tid.",
    cta: "Se vad som ingår",
    lockedItem:
      "Det här innehållet blir tillgängligt när ditt abonnemang är aktiverat.",
  },
  en: {
    title: "Included in your subscription",
    body: "Once your subscription is active you get access to all of Support and Health, with nutrition, training, behavioural support and tools to follow your health over time.",
    cta: "See what is included",
    lockedItem:
      "This content becomes available once your subscription is active.",
  },
} as const;

/**
 * The preview a patient WITHOUT subscription-content access gets of an
 * article: title, type, ordering and image metadata stay; body, steps,
 * ingredients, lessons, exercises, day plans, prompts and sources go. The
 * result is shaped like a PublicArticle so list consumers need one type, with
 * `locked: true` telling them why the content is empty. (Emptied recipe and
 * plan fields do not satisfy the authoring schema's min-length rules — a
 * preview is a projection for reading, never an article to be saved.)
 */
export function articlePreview(article: PublicArticle): PublicArticle {
  const {
    body: _body,
    cta: _cta,
    interaction: _interaction,
    sources: _sources,
    training,
    recipe,
    plan,
    ...rest
  } = article;
  return {
    ...rest,
    body: { sv: "", en: "" },
    locked: true,
    // A pass keeps its metadata (versions, counts, guidance is content).
    ...(training && training.role === "pass"
      ? { training: { ...training, restGuidance: null } }
      : {}),
    ...(recipe
      ? {
          recipe: {
            ...recipe,
            ingredients: [],
            steps: [],
            adaptations: [],
            smallAppetite: null,
          },
        }
      : {}),
    ...(plan
      ? {
          plan: {
            shortDescription: plan.shortDescription,
            intro: plan.intro,
            howToUse: "",
            days: [],
            extraSections: [],
          },
        }
      : {}),
  };
}
